import { Log } from '../../shared/log';
import { Injectable } from '@angular/core';
import { Headers, RequestOptions } from '@angular/http';
import { AuthHttp, AuthHttpError } from 'angular2-jwt';
import { VehicleRegistrationNumber } from '../../types/VehicleRegistrationNumber';
import {
  IAddress,
  IConcessionCard,
  IContactAge,
  IContactExtras,
  IEmailAddress,
  IEnableSharing,
  IGoCardDateLastVisible,
  IJpNumber,
  IMyDetails,
  IName,
  IPhoneNumber,
  ISelectedElectorate,
  IVehicleRegistration, IVehicleRegistrationNumber, INotificationPreferences
} from '../../interfaces/interfaces';
import { AppConfig } from '../../app.config';
import { BroadcastService } from '../broadcast/broadcast.service';

import 'rxjs/add/operator/toPromise';
import 'rxjs/add/operator/map';
import { ICoaServiceAndEvidenceModel } from '../../interfaces/ICoaServiceAndEvidenceModel';

const log = new Log('UserService');

@Injectable()
export class UserService {
  private serverUrl = this.appConfig.getValue('endPoint');

  private activityUrl = this.serverUrl + 'activity';
  private myDetailsUrl = this.serverUrl + 'mydetails';
  private namesUrl = this.serverUrl + 'names';
  private contactExtrasUrl = this.serverUrl + 'contactextras';
  private ageUrl = this.serverUrl + 'age';
  private genderUrl = this.serverUrl + 'gender';
  private emailAddressUrl = this.serverUrl + 'emailaddress';
  private phoneNumberUrl = this.serverUrl + 'phonenumber';
  private vehicleRegistrationUrl = this.serverUrl + 'vehicleRegistration';
  private vehicleRegistrationDetailsUrl = this.serverUrl + 'vehicleRegistrationDetails';
  private removeVehicleRegistrationUrl = this.serverUrl + 'removeVehicleRegistration';
  private addressesUrl = this.serverUrl + 'addresses';
  private enableSharingUrl = this.serverUrl + 'enablesharing';
  private goCardDateLastVisibleUrl = this.serverUrl + 'goCardDateLastVisible';
  private selectedElectorateUrl = this.serverUrl + 'selectedElectorate';
  private jpNumberUrl = this.serverUrl + 'jpNumber';
  private concessionCardsUrl = this.serverUrl + 'concessioncards';
  private paymentHistoryUrl = this.serverUrl + 'paymenthistory';
  private notificationPreferencesUrl = this.serverUrl + 'notificationPreferences';
  private serviceSubscriptionUrl = this.serverUrl + 'subscriptions';
  private serviceEvidenceUrl = this.serverUrl + 'serviceEvidence';

  handleError;

  constructor(
    private authHttp: AuthHttp,
    private appConfig: AppConfig,
    private broadcastService: BroadcastService
  ) {
    this.handleError = this.handleErrorMethod.bind(this);
  }

  getMyDetails(times): Promise<IMyDetails> {
    return this.authHttp
      .get(this.myDetailsUrl)
      .toPromise()
      .then(response => {
        return response.json() as IMyDetails
      })
      .catch((error) => {
        if (times - 1 > 0) {
          return this.getMyDetails(1)
        }
        return this.handleError
      });
  }

  getEmailAddress(): Promise<IEmailAddress> {
    return this.authHttp
      .get(this.emailAddressUrl)
      .toPromise()
      .then(response => response.json() as IEmailAddress)
      .catch(this.handleError);
  }

  getPreferredPhoneNumber(): Promise<IPhoneNumber> {
    return this.authHttp
      .get(this.phoneNumberUrl)
      .toPromise()
      .then(response => response.json() as IPhoneNumber)
      .catch(this.handleError);
  }

  getVehicleRegistration(): Promise<VehicleRegistrationNumber> {
    return this.authHttp
      .get(this.vehicleRegistrationUrl)
      .toPromise()
      .then(response => {
        switch (response.status) {
          case (204):
            return VehicleRegistrationNumber.empty();
          case (200):
            return new VehicleRegistrationNumber(response.text());
          default:
            throw new Error('');
        }
      })
      .catch(this.handleError);
  }

  getVehicleRegistrationDetails(): Promise<Array<IVehicleRegistration>> {
    return this.authHttp
      .get(this.vehicleRegistrationDetailsUrl)
      .toPromise()
      .then(response => response.json() as Array<IVehicleRegistration>)
      .catch(this.handleError);
  }

  getAddresses(): Promise<Array<IAddress>> {
    return this.authHttp
      .get(this.addressesUrl)
      .toPromise()
      .then(response => response.json() as Array<IAddress>)
      .catch(this.handleError);
  }

  addAddresses(model: IAddress[]): Promise<Object> {
    return this.authHttp.put(this.addressesUrl, { model })
      .toPromise()
      .then(response => {
        return response;
      })
      .catch(this.handleError);
  }

  getConcessionCards(): Promise<Array<IConcessionCard>> {
    return this.authHttp
      .get(this.concessionCardsUrl)
      .toPromise()
      .then(response => response.json() as Array<IConcessionCard>)
      .catch(this.handleError);
  }

  getContactExtras(times): Promise<IContactExtras> {
    return this.authHttp
      .get(this.contactExtrasUrl)
      .toPromise()
      .then(response => response.json() as IContactExtras)
      .catch((error) => {
          if (times - 1 > 0) {
            return this.getContactExtras(1)
          }
          return this.handleError
        });
  }

  getEnableSharing(times): Promise<Array<IEnableSharing>> {
    return this.authHttp
      .get(this.enableSharingUrl)
      .toPromise()
      .then(response => response.json() as Array<IEnableSharing>)
      .catch((error) => {
        if (times - 1 > 0) {
          return this.getEnableSharing(1)
        }
        return this.handleError
       });
  }

  getGoCardDateLastVisible(): Promise<IGoCardDateLastVisible> {
    return this.authHttp
      .get(this.goCardDateLastVisibleUrl)
      .toPromise()
      .then(response => response.json() as IGoCardDateLastVisible)
      .catch(this.handleError);
  }

  getSelectedElectorate(): Promise<ISelectedElectorate> {
    return this.authHttp
      .get(this.selectedElectorateUrl)
      .toPromise()
      .then(response => response.json() as ISelectedElectorate)
      .catch(this.handleError);
  }

  getJpNumber(): Promise<IJpNumber> {
    return this.authHttp
      .get(this.jpNumberUrl)
      .toPromise()
      .then(response => response.json() as IJpNumber)
      .catch(this.handleError);
  }

  getNotificationPreferences(): Promise<INotificationPreferences> {
    return this.authHttp
      .get(this.notificationPreferencesUrl)
      .toPromise()
      .then(response => response.json() as INotificationPreferences)
      .catch(this.handleError);
  }

  updateEmailAddress(model: IEmailAddress): Promise<Object> {
    return this.authHttp.put(this.emailAddressUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updatePhoneNumber(model: IPhoneNumber): Promise<Object> {
    return this.authHttp.put(this.phoneNumberUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  addVehicleRegistration(model: IVehicleRegistrationNumber): Promise<IVehicleRegistration> {
    return this.authHttp.put(this.vehicleRegistrationUrl, { model })
      .toPromise()
      .then(response => response.json() as IVehicleRegistration)
      .catch(this.handleError);
  }

  deleteVehicleRegistration(regoNumber) {
    return this.authHttp.delete(this.vehicleRegistrationUrl + '/' + regoNumber)
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updateContactExtras(model: IContactExtras): Promise<Object> {
    return this.authHttp.put(this.contactExtrasUrl, { model })
      .toPromise()
      .then(updatedUser => {
        return updatedUser;
      })
      .catch(this.handleError);
  }

  updateAge(model: IContactAge): Promise<Object> {
    return this.authHttp.put(this.ageUrl, { model })
      .toPromise()
      .then(updatedUser => {
        return updatedUser;
      })
      .catch(this.handleError);
  }

  updateConcessionCards(model: Array<IConcessionCard>): Promise<Object> {
    return this.authHttp.put(this.concessionCardsUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updateEnableSharing(model: Array<IEnableSharing>): Promise<Object> {
    return this.authHttp.put(this.enableSharingUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updateGoCardDateLastVisible(model: IGoCardDateLastVisible): Promise<Object> {
    return this.authHttp.put(this.goCardDateLastVisibleUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updateSelectedElectorate(model: ISelectedElectorate): Promise<Object> {
    return this.authHttp.put(this.selectedElectorateUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updateJpNumber(model: IJpNumber): Promise<Object> {
    return this.authHttp.put(this.jpNumberUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updateNames(model: IName): Promise<Object> {
    return this.authHttp.put(this.namesUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  updateGender(model: string): Promise<Object> {
    return this.authHttp
      .put(this.genderUrl, { model })
      .toPromise()
      .then(response => response )
      .catch(this.handleError)
  }

  updateNotificationPreferences(model: INotificationPreferences): Promise<Object> {
    return this.authHttp.put(this.notificationPreferencesUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  getActivity(pageNumber: number, maxItemsPerPage: number): Promise<any> {
    return this.authHttp
      .get(`${this.activityUrl}?pageNumber=${pageNumber}&maxItemsPerPage=${maxItemsPerPage}`)
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }

  getPostcode() {
    return this.getContactExtras(2)
      .then(response => response.postcode)
      .catch(this.handleError);
  }

  getLocation(): Promise<any> {
    return this.getContactExtras(2)
      .then(response => {
        return {
          locality: response.locality,
          postcode: response.postcode,
          latitude: response.latitude,
          longitude: response.longitude
        };
      })
      .catch(this.handleError);
  }

  updateSubscribedServicesAndEvidence(model: ICoaServiceAndEvidenceModel[]): Promise<Object> {
    return this.authHttp.put(this.serviceSubscriptionUrl, { model })
      .toPromise()
      .then(response => { return response; })
      .catch(this.handleError);
  }

  getSubscribedServiceEvidence() {
    return this.authHttp
      .get(this.serviceEvidenceUrl)
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }

  handleErrorMethod(error: any) {
    log.error(error);
    // check for token expiry
    if (error instanceof AuthHttpError) {
      const errorMessage = {
        message: 'Session Error',
        payload: error
      };
      this.broadcastService.broadcast(errorMessage);
    }
    return Promise.reject(error);
  }

}



// WEBPACK FOOTER //
// ./app/services/user/user.service.ts